import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  MessageCircle, 
  Calendar, 
  DollarSign, 
  User, 
  Phone,
  Mail,
  MapPin,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle
} from 'lucide-react';
import { apiService as api } from '../../services/api';

export const ReservasPage = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSubAdmin = user?.tipo === 'sub_admin';

  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todas');
  const [modalAprovar, setModalAprovar] = useState(false);
  const [reservaSelecionada, setReservaSelecionada] = useState(null);
  const [motosDisponiveis, setMotosDisponiveis] = useState([]);
  const [motoSelecionada, setMotoSelecionada] = useState('');
  const [processando, setProcessando] = useState(false);
  const [mostrarCadastroMoto, setMostrarCadastroMoto] = useState(false);
  const [planoEditado, setPlanoEditado] = useState('');
  const [dataInicioLocacao, setDataInicioLocacao] = useState(new Date().toISOString().split('T')[0]);
  const [novaMoto, setNovaMoto] = useState({
    placa: '',
    cor: '',
    km_atual: 0,
    ano: new Date().getFullYear(),
    data_aquisicao: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    carregarReservas();
  }, []);

  const carregarReservas = async () => {
    try {
      console.log('🔄 Carregando reservas...');
      const reservasData = await api.getReservas();
      console.log('✅ Reservas carregadas:', reservasData);
      console.log('📊 Total de reservas:', reservasData.length);
      setReservas(reservasData);
    } catch (error) {
      console.error('❌ Erro ao carregar reservas:', error);
      console.error('❌ Detalhes do erro:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const abrirWhatsApp = (reserva) => {
    const mensagem = `Olá ${reserva.nome_completo}! Vi sua reserva de moto na Desloca.

*Detalhes da Reserva:*
• Plano: ${reserva.plano_escolhido}
• Cor: ${reserva.cor_moto_desejada}
• Retirada: ${reserva.tipo_retirada === 'imediato' ? 'Imediata' : reserva.data_retirada}

Vamos finalizar sua reserva?`;

    window.open(`https://wa.me/${reserva.telefone.replace(/\D/g, '')}?text=${encodeURIComponent(mensagem)}`, '_blank');
    
    atualizarStatus(reserva.id, 'contatado');
  };

  const atualizarStatus = async (id, novoStatus) => {
    try {
      await api.put(`/reservas/${id}`, { status: novoStatus });
      carregarReservas();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
    }
  };

  const excluirReserva = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta reserva?')) return;
    
    try {
      await api.deletarReserva(id);
      alert('Reserva excluída com sucesso!');
      carregarReservas();
    } catch (error) {
      console.error('Erro ao excluir reserva:', error);
      alert('Erro ao excluir reserva. Tente novamente.');
    }
  };

  const iniciarAprovacao = async (reserva) => {
    try {
      setReservaSelecionada(reserva);
      setProcessando(true);
      
      // Buscar motos disponíveis usando a função correta
      const motos = await api.getMotos();
      const disponiveis = motos.filter(m => m.status === 'disponivel');
      
      setMotosDisponiveis(disponiveis);
      setModalAprovar(true);
      setProcessando(false);
      
      // Resetar data de início para hoje
      setDataInicioLocacao(new Date().toISOString().split('T')[0]);
      
      // Se não houver motos, mostrar diretamente o formulário de cadastro
      if (disponiveis.length === 0) {
        setMostrarCadastroMoto(true);
      }
    } catch (error) {
      console.error('Erro ao buscar motos:', error);
      alert(`Erro ao buscar motos: ${error.message}`);
      setProcessando(false);
    }
  };

  const cadastrarNovaMoto = async () => {
    if (!novaMoto.placa || !novaMoto.cor) {
      alert('Por favor, preencha placa e cor da moto!');
      return;
    }

    setProcessando(true);
    try {
      const motoCriada = await api.criarMoto({
        ...novaMoto,
        modelo: 'DK 160',
        status: 'disponivel'
      });
      
      // Adicionar à lista de motos disponíveis
      setMotosDisponiveis([...motosDisponiveis, motoCriada]);
      
      // Selecionar automaticamente a moto recém-criada
      setMotoSelecionada(motoCriada.id);
      
      // Fechar formulário de cadastro
      setMostrarCadastroMoto(false);
      
      // Resetar formulário
      setNovaMoto({
        placa: '',
        cor: '',
        km_atual: 0,
        ano: new Date().getFullYear(),
        data_aquisicao: new Date().toISOString().split('T')[0]
      });
      
      alert('✅ Moto cadastrada com sucesso!');
    } catch (error) {
      console.error('Erro ao cadastrar moto:', error);
      alert(`Erro ao cadastrar moto: ${error.response?.data?.detail || error.message}`);
    } finally {
      setProcessando(false);
    }
  };

  const confirmarAprovacao = async () => {
    if (!motoSelecionada) {
      alert('Por favor, selecione uma moto!');
      return;
    }

    setProcessando(true);
    let locacaoCriada = null;
    let clienteFinal = null;
    const soDigitos = (v) => String(v || '').replace(/\D/g, '');
    
    try {
      // 1. Verificar se cliente já existe pelo CPF (comparação normalizada)
      console.log('🔍 Verificando se cliente já existe...');
      
      try {
        const todosClientes = await api.getClientes();
        const cpfReserva = soDigitos(reservaSelecionada.cpf);
        const clienteExistente = todosClientes.find(c => soDigitos(c.cpf) === cpfReserva);
        
        if (clienteExistente) {
          console.log('✅ Cliente já existe, usando cadastro existente:', clienteExistente);
          clienteFinal = clienteExistente;
        }
      } catch (error) {
        console.log('Cliente não encontrado, será criado novo cadastro');
      }
      
      // 2. Se não existe, cadastrar novo cliente
      if (!clienteFinal) {
        console.log('📝 Cadastrando novo cliente...');
        
        try {
          // Extrair CEP do endereço se houver, senão usar padrão
          const cep = reservaSelecionada.endereco.match(/\d{5}-?\d{3}/) ? 
                      reservaSelecionada.endereco.match(/\d{5}-?\d{3}/)[0] : '11700-000';
          
          const clienteData = {
            nome: reservaSelecionada.nome_completo,
            cpf: reservaSelecionada.cpf,
            data_nascimento: reservaSelecionada.data_nascimento,
            telefone: reservaSelecionada.telefone,
            email: reservaSelecionada.email || `${reservaSelecionada.cpf.replace(/\D/g, '')}@desloca.temp`,
            endereco: reservaSelecionada.endereco,
            cidade: 'Praia Grande',
            cep: cep,
            cnh: reservaSelecionada.numero_cnh,
            profissao: reservaSelecionada.profissao || '',
            estado_civil: reservaSelecionada.estado_civil || '',
            observacoes: `Cliente cadastrado via reserva online. Validade CNH: ${reservaSelecionada.validade_cnh}`
          };
          
          console.log('Dados do cliente:', clienteData);
          clienteFinal = await api.criarCliente(clienteData);
          console.log('✅ Cliente cadastrado:', clienteFinal);
        } catch (clienteError) {
          console.error('❌ Erro ao cadastrar cliente:', clienteError);
          const detail = clienteError.response?.data?.detail;
          // Auto-recuperação: backend informa que o CPF já existe (409) e
          // devolve o cadastro existente — usa ele e segue a aprovação.
          if (detail && typeof detail === 'object' && detail.cliente_existente) {
            console.log('♻️ CPF já cadastrado — usando cadastro existente:', detail.cliente_existente);
            clienteFinal = detail.cliente_existente;
          } else {
            const msg = typeof detail === 'object' ? (detail?.message || JSON.stringify(detail)) : (detail || clienteError.message);
            throw new Error(`Falha ao cadastrar cliente: ${msg}`);
          }
        }
      }
      
      // 3. Criar locação
      console.log('🏍️ Criando locação...');
      
      try {
        // Buscar dados completos da moto selecionada para obter km_atual
        const motoDetalhes = motosDisponiveis.find(m => m.id === motoSelecionada);
        if (!motoDetalhes) {
          throw new Error('Moto não encontrada');
        }
        
        // Calcular data_fim baseado no plano escolhido e data de início selecionada
        const planoEscolhido = planoEditado || reservaSelecionada.plano_escolhido;
        const dataInicio = new Date(dataInicioLocacao);
        let dataFim = new Date(dataInicio);
        
        if (planoEscolhido === 'mensal') {
          dataFim.setDate(dataFim.getDate() + 30);
        } else if (planoEscolhido === 'anual') {
          dataFim.setDate(dataFim.getDate() + 365);
        } else if (planoEscolhido === 'fidelidade') {
          dataFim.setDate(dataFim.getDate() + 365);
        }
        
        const dataFimFormatada = dataFim.toISOString().split('T')[0];
        
        const locacaoData = {
          cliente_id: clienteFinal.id,
          moto_id: motoSelecionada,
          data_inicio: dataInicioLocacao,
          data_fim: dataFimFormatada,
          km_inicial: motoDetalhes.km_atual || 0,
          plano: planoEscolhido,
          valor_caucao: reservaSelecionada.valor_caucao,
          observacoes: `Criado automaticamente da reserva online. Cor preferida: ${reservaSelecionada.cor_moto_desejada}`
        };
        
        console.log('Dados da locação:', locacaoData);
        locacaoCriada = await api.criarLocacao(locacaoData);
        console.log('✅ Locação criada com sucesso:', locacaoCriada);
      } catch (locacaoError) {
        console.error('❌ Erro ao criar locação:', locacaoError);
        const dLoc = locacaoError.response?.data?.detail;
        const msgLoc = typeof dLoc === 'object' ? (dLoc?.message || JSON.stringify(dLoc)) : (dLoc || locacaoError.message);
        throw new Error(`Falha ao criar locação: ${msgLoc}`);
      }
      
      // 4. Atualizar status da reserva para aprovado
      console.log('📝 Atualizando status da reserva...');
      try {
        await api.atualizarReserva(reservaSelecionada.id, { status: 'aprovado' });
        console.log('✅ Reserva atualizada para status aprovado');
      } catch (reservaError) {
        // IMPORTANTE: Se a locação já foi criada, não mostrar erro
        // apenas logar o problema com a atualização da reserva
        console.warn('⚠️ Locação criada, mas houve problema ao atualizar reserva:', reservaError);
        console.warn('⚠️ Detalhes:', reservaError.response?.data);
        // Continuar normalmente pois a operação principal (criar locação) foi bem-sucedida
      }
      
      // Sucesso! Mostrar mensagem positiva
      alert(`✅ Sucesso! ${clienteFinal ? 'Cliente vinculado' : 'Cliente cadastrado'} e locação criada!`);
      
      // Fechar modal e recarregar
      setModalAprovar(false);
      setReservaSelecionada(null);
      setMotoSelecionada('');
      setPlanoEditado('');
      setDataInicioLocacao(new Date().toISOString().split('T')[0]);
      carregarReservas();
      
    } catch (error) {
      console.error('❌ Erro ao aprovar reserva:', error);
      console.error('❌ Detalhes:', error.response?.data);
      
      // Mostrar mensagem de erro apropriada
      alert(`Erro ao aprovar reserva: ${error.message || error.response?.data?.detail || 'Erro desconhecido'}`);
    } finally {
      setProcessando(false);
    }
  };

  const getStatusColor = (status) => {
    const cores = {
      pendente: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      contatado: 'bg-blue-100 text-blue-800 border-blue-200',
      aprovado: 'bg-green-100 text-green-800 border-green-200',
      cancelado: 'bg-red-100 text-red-800 border-red-200'
    };
    return cores[status] || cores.pendente;
  };

  const getStatusIcon = (status) => {
    const icones = {
      pendente: AlertCircle,
      contatado: Clock,
      aprovado: CheckCircle,
      cancelado: XCircle
    };
    const Icon = icones[status] || AlertCircle;
    return <Icon className="w-4 h-4" />;
  };

  const reservasFiltradas = filtro === 'todas' 
    ? reservas 
    : reservas.filter(r => r.status === filtro);

  const contagemStatus = {
    todas: reservas.length,
    pendente: reservas.filter(r => r.status === 'pendente').length,
    contatado: reservas.filter(r => r.status === 'contatado').length,
    aprovado: reservas.filter(r => r.status === 'aprovado').length,
    cancelado: reservas.filter(r => r.status === 'cancelado').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">Reservas Online</h1>
        <p className="text-gray-300">Gerencie as solicitações de reserva dos clientes</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { value: 'todas', label: 'Todas', count: contagemStatus.todas },
          { value: 'pendente', label: 'Pendentes', count: contagemStatus.pendente },
          { value: 'contatado', label: 'Contatadas', count: contagemStatus.contatado },
          { value: 'aprovado', label: 'Aprovadas', count: contagemStatus.aprovado },
          { value: 'cancelado', label: 'Canceladas', count: contagemStatus.cancelado }
        ].map((item) => (
          <Button
            key={item.value}
            onClick={() => setFiltro(item.value)}
            variant={filtro === item.value ? 'default' : 'outline'}
            className={filtro === item.value ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            {item.label}
            {item.count > 0 && (
              <Badge className="ml-2 bg-white text-emerald-400">{item.count}</Badge>
            )}
          </Button>
        ))}
      </div>

      {reservasFiltradas.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-300">Nenhuma reserva encontrada</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {reservasFiltradas.map((reserva) => (
            <Card key={reserva.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
                <div className="flex justify-between items-start flex-wrap gap-4">
                  <div>
                    <CardTitle className="text-xl mb-2 text-gray-800">{reserva.nome_completo}</CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge className={`${getStatusColor(reserva.status)} border`}>
                        {getStatusIcon(reserva.status)}
                        <span className="ml-1 capitalize">{reserva.status}</span>
                      </Badge>
                      <span className="text-sm text-gray-500">
                        {new Date(reserva.data_criacao).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  </div>
                  <Button
                    onClick={() => abrirWhatsApp(reserva)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Chamar no WhatsApp
                  </Button>
                </div>
              </CardHeader>

              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="space-y-3">
                    <h4 className="font-semibold text-white flex items-center gap-2">
                      <User className="w-4 h-4 text-emerald-400" />
                      Dados Pessoais
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-300">
                        <Phone className="w-4 h-4" />
                        <span>{reserva.telefone}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-300">
                        <Mail className="w-4 h-4" />
                        <span className="truncate">{reserva.email}</span>
                      </div>
                      <div className="flex items-start gap-2 text-gray-300">
                        <MapPin className="w-4 h-4 mt-1" />
                        <span>{reserva.endereco}</span>
                      </div>
                      <div className="text-gray-300">
                        <strong>CPF:</strong> {reserva.cpf}
                      </div>
                      <div className="text-gray-300">
                        <strong>Nascimento:</strong> {new Date(reserva.data_nascimento).toLocaleDateString('pt-BR')}
                      </div>
                      <div className="text-gray-300">
                        <strong>CNH:</strong> {reserva.numero_cnh}
                      </div>
                      <div className="text-gray-300">
                        <strong>Validade CNH:</strong> {new Date(reserva.validade_cnh).toLocaleDateString('pt-BR')}
                      </div>
                      {reserva.profissao && (
                        <div className="text-gray-300">
                          <strong>Profissão:</strong> {reserva.profissao}
                        </div>
                      )}
                      {reserva.estado_civil && (
                        <div className="text-gray-300">
                          <strong>Estado Civil:</strong> {reserva.estado_civil}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-white flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-emerald-400" />
                      Detalhes da Reserva
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-400">Plano:</span>
                        <span className="ml-2 font-medium capitalize text-white">{reserva.plano_escolhido}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Cor da Moto:</span>
                        <span className="ml-2 font-medium capitalize text-white">{reserva.cor_moto_desejada}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Tipo de Retirada:</span>
                        <span className="ml-2 font-medium capitalize text-white">{reserva.tipo_retirada}</span>
                      </div>
                      {reserva.data_retirada && (
                        <div>
                          <span className="text-gray-400">Data de Retirada:</span>
                          <span className="ml-2 font-medium text-white">
                            {new Date(reserva.data_retirada).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      )}
                      {reserva.observacoes && (
                        <div className="mt-2">
                          <span className="text-gray-400">Observações:</span>
                          <p className="text-gray-200 mt-1">{reserva.observacoes}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-white flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-emerald-400" />
                      Valores
                    </h4>
                    <div className="bg-green-50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Caução:</span>
                        <span className="font-semibold text-gray-900">R$ {reserva.valor_caucao.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">1ª Semana:</span>
                        <span className="font-semibold text-gray-900">R$ {reserva.valor_primeira_semana.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-green-200">
                        <span className="font-bold text-gray-900">Total:</span>
                        <span className="font-bold text-emerald-600">
                          R$ {(reserva.valor_caucao + reserva.valor_primeira_semana).toFixed(2)}
                        </span>
                      </div>
                      <div className="pt-2">
                        {reserva.confirmou_pagamento ? (
                          <Badge className="bg-green-100 text-green-800 border-green-200">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Pagamento Confirmado
                          </Badge>
                        ) : (
                          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Pagamento Pendente
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-white/10 flex flex-wrap gap-2">
                  {!isSubAdmin && (
                    <>
                      <Button
                        data-testid={`btn-marcar-contatado-${reserva.id}`}
                        onClick={() => atualizarStatus(reserva.id, 'contatado')}
                        variant="outline"
                        disabled={reserva.status !== 'pendente'}
                        className="text-blue-400 border-blue-600 hover:bg-blue-50"
                      >
                        Marcar como Contatado
                      </Button>
                      <Button
                        data-testid={`btn-aprovar-reserva-${reserva.id}`}
                        onClick={() => iniciarAprovacao(reserva)}
                        variant="outline"
                        disabled={reserva.status === 'aprovado' || processando}
                        className="text-emerald-400 border-green-600 hover:bg-green-50"
                      >
                        {processando ? 'Processando...' : 'Aprovar Reserva'}
                      </Button>
                      <Button
                        data-testid={`btn-cancelar-reserva-${reserva.id}`}
                        onClick={() => atualizarStatus(reserva.id, 'cancelado')}
                        variant="outline"
                        disabled={reserva.status === 'cancelado'}
                        className="text-rose-400 border-red-600 hover:bg-red-50"
                      >
                        Cancelar
                      </Button>
                      <Button
                        data-testid={`btn-excluir-reserva-${reserva.id}`}
                        onClick={() => excluirReserva(reserva.id)}
                        variant="outline"
                        className="text-gray-300 border-gray-600 hover:bg-slate-800/50 ml-auto"
                      >
                        Excluir
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal de Aprovação com Seleção de Moto */}
      {modalAprovar && reservaSelecionada && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800/90 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4 text-emerald-400">
                ✅ Aprovar Reserva e Criar Locação
              </h2>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold mb-2">📋 Dados do Cliente</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><strong>Nome:</strong> {reservaSelecionada.nome_completo}</div>
                  <div><strong>CPF:</strong> {reservaSelecionada.cpf}</div>
                  <div><strong>CNH:</strong> {reservaSelecionada.numero_cnh}</div>
                  <div><strong>Telefone:</strong> {reservaSelecionada.telefone}</div>
                  <div className="col-span-2"><strong>Email:</strong> {reservaSelecionada.email}</div>
                  <div className="col-span-2"><strong>Endereço:</strong> {reservaSelecionada.endereco}</div>
                  <div><strong>Profissão:</strong> {reservaSelecionada.profissao || 'Não informado'}</div>
                  <div><strong>Estado Civil:</strong> {reservaSelecionada.estado_civil || 'Não informado'}</div>
                </div>
              </div>

              {/* Perfil e Questionário */}
              {(reservaSelecionada.uso_principal || reservaSelecionada.horas_uso_dia) && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold mb-3 text-emerald-300 flex items-center gap-2">
                    <span>📊</span> Perfil do Cliente
                  </h3>
                  
                  <div className="space-y-3 text-sm">
                    {/* Uso e Rotina */}
                    {(reservaSelecionada.uso_principal || reservaSelecionada.horas_uso_dia || reservaSelecionada.tempo_permanencia) && (
                      <div className="border-l-4 border-green-400 pl-3">
                        <div className="font-semibold text-gray-200 mb-1">🏍️ Uso e Rotina</div>
                        {reservaSelecionada.uso_principal && (
                          <div className="text-gray-300">
                            <strong>Uso principal:</strong> {
                              reservaSelecionada.uso_principal === 'trabalho_fixo' ? 'Trabalho fixo' :
                              reservaSelecionada.uso_principal === 'aplicativos' ? 'Aplicativos' :
                              reservaSelecionada.uso_principal === 'uso_pessoal' ? 'Uso pessoal' :
                              reservaSelecionada.uso_principal_outro || 'Outro'
                            }
                          </div>
                        )}
                        {reservaSelecionada.horas_uso_dia && (
                          <div className="text-gray-300">
                            <strong>Horas/dia:</strong> {
                              reservaSelecionada.horas_uso_dia === 'ate_4h' ? 'Até 4h' :
                              reservaSelecionada.horas_uso_dia === '4h_8h' ? 'De 4h a 8h' :
                              'Mais de 8h'
                            }
                          </div>
                        )}
                        {reservaSelecionada.tempo_permanencia && (
                          <div className="text-gray-300">
                            <strong>Permanência:</strong> {
                              reservaSelecionada.tempo_permanencia === 'ate_1mes' ? 'Até 1 mês' :
                              reservaSelecionada.tempo_permanencia === '1_3meses' ? '1 a 3 meses' :
                              'Longo prazo'
                            }
                          </div>
                        )}
                      </div>
                    )}

                    {/* Perfil e Responsabilidade */}
                    {(reservaSelecionada.outras_pessoas_conduzem || reservaSelecionada.cuidado_veiculo) && (
                      <div className="border-l-4 border-blue-400 pl-3">
                        <div className="font-semibold text-gray-200 mb-1">👤 Perfil</div>
                        {reservaSelecionada.outras_pessoas_conduzem && (
                          <div className="text-gray-300">
                            <strong>Outras pessoas conduzem:</strong> {reservaSelecionada.outras_pessoas_conduzem === 'sim' ? 'Sim' : 'Não'}
                          </div>
                        )}
                        {reservaSelecionada.cuidado_veiculo && (
                          <div className="text-gray-300">
                            <strong>Cuidado com veículos:</strong> {
                              reservaSelecionada.cuidado_veiculo === 'zelo_manutencao' ? 'Zelo e manutenção' :
                              reservaSelecionada.cuidado_veiculo === 'uso_necessario' ? 'Uso necessário' :
                              'Terceiriza cuidados'
                            }
                          </div>
                        )}
                      </div>
                    )}

                    {/* Objetivo */}
                    {(reservaSelecionada.objetivo_locacao || reservaSelecionada.interesse_fidelidade) && (
                      <div className="border-l-4 border-purple-400 pl-3">
                        <div className="font-semibold text-gray-200 mb-1">🎯 Objetivo</div>
                        {reservaSelecionada.objetivo_locacao && (
                          <div className="text-gray-300">
                            <strong>Objetivo:</strong> {
                              reservaSelecionada.objetivo_locacao === 'trabalhar_gerar_renda' ? 'Trabalhar e gerar renda' :
                              reservaSelecionada.objetivo_locacao === 'temporario_ate_comprar' ? 'Temporário até comprar' :
                              reservaSelecionada.objetivo_locacao === 'fidelidade_conquistar' ? 'Plano Fidelidade' :
                              reservaSelecionada.objetivo_locacao_outro || 'Outro'
                            }
                          </div>
                        )}
                        {reservaSelecionada.interesse_fidelidade && (
                          <div className="text-gray-300">
                            <strong>Interesse em fidelidade:</strong> {
                              reservaSelecionada.interesse_fidelidade === 'sim_quero' ? '✅ Sim, quer participar' :
                              reservaSelecionada.interesse_fidelidade === 'talvez' ? '🤔 Talvez' :
                              '⏱️ Apenas curto prazo'
                            }
                          </div>
                        )}
                      </div>
                    )}

                    {/* Expectativas */}
                    {(reservaSelecionada.como_conheceu || reservaSelecionada.compromisso_pagamento) && (
                      <div className="border-l-4 border-yellow-400 pl-3">
                        <div className="font-semibold text-gray-200 mb-1">💬 Expectativas</div>
                        {reservaSelecionada.como_conheceu && (
                          <div className="text-gray-300">
                            <strong>Conheceu via:</strong> {
                              reservaSelecionada.como_conheceu === 'indicacao' ? '👥 Indicação' :
                              reservaSelecionada.como_conheceu === 'instagram' ? '📸 Instagram' :
                              reservaSelecionada.como_conheceu === 'google' ? '🔍 Google' :
                              reservaSelecionada.como_conheceu_outro || 'Outro'
                            }
                          </div>
                        )}
                        {reservaSelecionada.compromisso_pagamento && (
                          <div className="text-gray-300">
                            <strong>Compromisso:</strong> {
                              reservaSelecionada.compromisso_pagamento === 'sempre_cumpro' ? '⭐ Sempre cumpre' :
                              reservaSelecionada.compromisso_pagamento === 'as_vezes_atraso' ? '⚠️ Às vezes atrasa' :
                              '🤝 Prefere combinar'
                            }
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold mb-3">🏍️ Preferências da Reserva</h3>
                <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                  <div><strong>Cor Preferida:</strong> {reservaSelecionada.cor_moto_desejada}</div>
                  <div><strong>Tipo Retirada:</strong> {reservaSelecionada.tipo_retirada === 'imediato' ? 'Imediata' : 'Agendada'}</div>
                  <div><strong>Caução:</strong> R$ {reservaSelecionada.valor_caucao}</div>
                  <div><strong>Plano Original:</strong> {reservaSelecionada.plano_escolhido}</div>
                </div>
                
                {/* Editar Plano */}
                <div className="border-t border-yellow-300 pt-3">
                  <label className="block font-semibold text-sm mb-2">
                    ✏️ Alterar Plano (se necessário):
                  </label>
                  <select
                    value={planoEditado || reservaSelecionada.plano_escolhido}
                    onChange={(e) => setPlanoEditado(e.target.value)}
                    className="w-full border-2 border-yellow-300 rounded px-3 py-2 bg-white"
                  >
                    <option value="mensal">Mensal - R$ 400,00/semana (Carência 3 meses - Troca de óleo inclusa)</option>
                    <option value="anual">Anual - R$ 349,99/semana (Carência 1 ano - Troca de óleo inclusa)</option>
                    <option value="fidelidade">Fidelidade - R$ 425,00/semana (Tudo incluso + Moto Zero Km)</option>
                  </select>
                  {planoEditado && planoEditado !== reservaSelecionada.plano_escolhido && (
                    <p className="text-xs text-orange-600 mt-1">
                      ⚠️ Plano será alterado de &ldquo;{reservaSelecionada.plano_escolhido}&rdquo; para &ldquo;{planoEditado}&rdquo;
                    </p>
                  )}
                </div>
                
                {/* Data de Início da Locação */}
                <div className="border-t border-yellow-300 pt-3 mt-3">
                  <label className="block font-semibold text-sm mb-2">
                    📅 Data de Início da Locação:
                  </label>
                  <input
                    type="date"
                    value={dataInicioLocacao}
                    onChange={(e) => setDataInicioLocacao(e.target.value)}
                    className="w-full border-2 border-yellow-300 rounded px-3 py-2 bg-white"
                  />
                  <p className="text-xs text-gray-300 mt-1">
                    💡 Dica: Se for uma locação passada, selecione a data correta para gerar o histórico automaticamente
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <label className="block font-semibold text-lg">
                    🏍️ Selecione a Moto para Locação:
                  </label>
                  <Button
                    onClick={() => setMostrarCadastroMoto(!mostrarCadastroMoto)}
                    variant="outline"
                    className="text-sm"
                  >
                    {mostrarCadastroMoto ? 'Ver Lista' : '+ Cadastrar Nova Moto'}
                  </Button>
                </div>

                {/* Formulário de Cadastro de Nova Moto */}
                {mostrarCadastroMoto ? (
                  <div className="border-2 border-blue-300 bg-blue-50 rounded-lg p-4 space-y-3">
                    <h3 className="font-semibold text-blue-800 mb-3">📝 Cadastrar Nova Moto</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">Placa *</label>
                        <input
                          type="text"
                          value={novaMoto.placa}
                          onChange={(e) => setNovaMoto({...novaMoto, placa: e.target.value.toUpperCase()})}
                          placeholder="ABC-1234 ou UEG-0A20"
                          maxLength={8}
                          className="w-full border rounded px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Cor *</label>
                        <select
                          value={novaMoto.cor}
                          onChange={(e) => setNovaMoto({...novaMoto, cor: e.target.value})}
                          className="w-full border rounded px-3 py-2"
                        >
                          <option value="">Selecione...</option>
                          <option value="Preta">Preta</option>
                          <option value="Branca">Branca</option>
                          <option value="Vermelha">Vermelha</option>
                          <option value="Azul">Azul</option>
                          <option value="Prata">Prata</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">KM Atual</label>
                        <input
                          type="number"
                          value={novaMoto.km_atual}
                          onChange={(e) => setNovaMoto({...novaMoto, km_atual: parseInt(e.target.value)})}
                          className="w-full border rounded px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Ano</label>
                        <input
                          type="number"
                          value={novaMoto.ano}
                          onChange={(e) => setNovaMoto({...novaMoto, ano: parseInt(e.target.value)})}
                          className="w-full border rounded px-3 py-2"
                        />
                      </div>
                    </div>
                    <Button
                      onClick={cadastrarNovaMoto}
                      disabled={!novaMoto.placa || !novaMoto.cor || processando}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {processando ? 'Cadastrando...' : '✅ Cadastrar e Selecionar'}
                    </Button>
                  </div>
                ) : (
                  /* Lista de Motos Disponíveis */
                  <div className="grid grid-cols-1 gap-3 max-h-64 overflow-y-auto">
                    {motosDisponiveis.length === 0 ? (
                      <div className="text-center py-8 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                        <p className="text-yellow-800 font-semibold mb-2">⚠️ Nenhuma moto disponível!</p>
                        <p className="text-sm text-yellow-700 mb-3">Cadastre uma nova moto para continuar</p>
                        <Button
                          onClick={() => setMostrarCadastroMoto(true)}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          + Cadastrar Primeira Moto
                        </Button>
                      </div>
                    ) : (
                      motosDisponiveis.map(moto => (
                        <div
                          key={moto.id}
                          onClick={() => setMotoSelecionada(moto.id)}
                          className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                            motoSelecionada === moto.id
                              ? 'border-green-500 bg-green-50'
                              : 'border-white/10 hover:border-green-300'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="font-bold text-lg">{moto.placa}</div>
                              <div className="text-sm text-gray-300">
                                DK 160 - {moto.cor} | {moto.km_atual} km
                              </div>
                            </div>
                            {motoSelecionada === moto.id && (
                              <CheckCircle className="w-8 h-8 text-green-500" />
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-200">
                  <strong>⚠️ Ao confirmar, o sistema irá:</strong>
                </p>
                <ul className="list-disc list-inside text-sm text-gray-200 mt-2 space-y-1">
                  <li>Cadastrar o cliente automaticamente</li>
                  <li>Criar a locação com início em <strong>{new Date(dataInicioLocacao).toLocaleDateString('pt-BR')}</strong></li>
                  <li>Marcar a reserva como aprovada</li>
                  <li>Gerar histórico de pagamentos e manutenções desde a data de início</li>
                  <li>Iniciar cobranças semanais automaticamente</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={confirmarAprovacao}
                  disabled={!motoSelecionada || processando}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  {processando ? 'Processando...' : '✅ Confirmar e Criar Locação'}
                </Button>
                <Button
                  onClick={() => {
                    setModalAprovar(false);
                    setReservaSelecionada(null);
                    setMotoSelecionada('');
                  }}
                  disabled={processando}
                  variant="outline"
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
